import React, { useState } from 'react'
import Button from '@mui/material/Button'
import { ENTITY_TYPES, LocationDTO } from '@sales-center/common'
import { EntityDetail } from '@user/store/map/typings'
import { ErrorModalState } from './hooks'
import { AddPaymentMethodDialog } from '@user/components/Payments/AddPaymentMethodDialog/AddPaymentMethodDialog'
import { useDialogState } from '@user/utils/useDialogState'
import { checkIsClosed } from '../utils'
import CircularProgress from '@mui/material/CircularProgress'

type Props = {
  entityType: ENTITY_TYPES
  entityDetails: EntityDetail
  canUpdate: boolean
  isClosed: boolean
  serviceLocation: LocationDTO
  setErrorModal: (errorModal: ErrorModalState) => void
  refetchEntityDetails: () => Promise<EntityDetail | null>
}

const CARD_LOCATION_ERROR =
  'You must match this record to a PestPac location before you can capture payment information.'

export function CardCapture({
  entityType,
  entityDetails,
  canUpdate,
  setErrorModal,
  refetchEntityDetails,
}: Props) {
  const [open, onOpen, onClose] = useDialogState()
  const [isLoading, setIsLoading] = useState(false)

  async function handleViewClick() {
    setIsLoading(true)
    const freshEntityDetails = await refetchEntityDetails()
    setIsLoading(false)
    if (!canUpdate) {
      setErrorModal({
        open: true,
        title: 'Card Capture',
        message: "You don't have permission to perform this action.",
      })
      return
    }

    const currentIsClosed = checkIsClosed(freshEntityDetails?.salesFunnelStage)
    if (currentIsClosed) {
      setErrorModal({
        open: true,
        title: 'Card Capture',
        message: 'You cannot capture payment information for a closed record.',
      })
      return
    }

    if (!freshEntityDetails?.externalLocationId) {
      setErrorModal({
        open: true,
        title: 'Card Capture',
        message: CARD_LOCATION_ERROR,
        showPestPacActions: true,
        onRetry: handleViewClick,
      })
      return
    }
    onOpen()
  }

  return (
    <>
      <Button
        fullWidth
        size="large"
        variant="contained"
        sx={{
          backgroundColor: '#D4E3F3',
        }}
        onClick={handleViewClick}
        color="secondary"
      >
        {isLoading ? <CircularProgress size={20} /> : 'Capture Card'}
      </Button>

      <AddPaymentMethodDialog
        entityId={entityDetails?.id}
        entityType={entityType}
        locationId={entityDetails?.externalLocationId}
        onClose={onClose}
        open={open}
      />
    </>
  )
}
